import { useCallback, useEffect, useState } from "react";
import axios from "axios";

export function useTweets(keywords?: string[]) {
    const keywordQuery = keywords?.join(" ");
    const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
    const [searchAfter, setSearchAfter] = useState<string | undefined>(undefined);
    const [tweets, setTweets] = useState<Tweet[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const endpoint = new URL("./tweets", API_HOST).toString();
    const fetch = useCallback(async () => {
        try {
            setLoading(true);
            setHasNext(true);
            setTotalCount(undefined);
            setSearchAfter(undefined);
            const { data } = await axios.get<SearchResult>(endpoint, {
                params: {
                    keywords: keywordQuery,
                },
            });
            setTotalCount(data.total_count);
            setTweets(data.tweets);
            setSearchAfter(data.search_after);
            setHasNext(data.tweets.length >= 50); // TODO: limitを与えられるようにする
        } catch (error) {
            console.error(error);
            setHasNext(false);
        } finally {
            setLoading(false);
        }
    }, [keywordQuery]);

    const fetchNext = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get<SearchResult>(endpoint, {
                params: {
                    keywords: keywordQuery,
                    search_after: searchAfter,
                },
            });
            setTweets(tweets.concat(data.tweets));
            setSearchAfter(data.search_after);
            setHasNext(data.tweets.length >= 50); // TODO: limitを与えられるようにする
        } catch (error) {
            console.error(error);
            setHasNext(false);
        } finally {
            setLoading(false);
        }
    }, [keywordQuery, tweets, searchAfter]);

    useEffect(() => {
        fetch();
    }, [keywordQuery]);

    return { tweets, totalCount, loading, hasNext, fetchNext };
}

type SearchResult = {
    tweets: Tweet[];
    total_count: number;
    search_after?: string;
};

export type Tweet = {
    coordinates?: [number, number];
    created_at: Date;
    current_user_retweet?: number;
    display_text_range?: [number, number];
    entities: TweetEntities;
    extended_entities?: ExtendedTweetEntities;
    favorite_count: number;
    favorited?: boolean;
    filter_level?: FilterLevel;
    id: number;
    id_str: string;
    in_reply_to_user_id?: number;
    in_reply_to_user_id_str?: string;
    in_reply_to_screen_name?: string;
    in_reply_to_status_id?: number;
    in_reply_to_status_id_str?: string;
    lang?: string;
    possibly_sensitive?: boolean;
    quoted_status?: Tweet;
    quoted_status_id?: number;
    quoted_status_id_str?: string;
    retweet_count: number;
    retweeted?: boolean;
    retweeted_status?: Tweet;
    source?: string;
    text: string;
    truncated: boolean;
    user?: TwitterUser;
    withheld_copyright: boolean;
    withheld_in_countries?: string[];
    withheld_scope?: string;
};

type FilterLevel = "none" | "low" | "medium";

type TweetEntities = {
    hashtags: HashtagEntity[];
    symbols?: HashtagEntity[];
    urls: UrlEntity[];
    user_mentions: MentionEntity[];
    media?: MediaEntity[];
};

type ExtendedTweetEntities = {
    media: MediaEntity[];
};

type HashtagEntity = {
    indices: [number, number];
    text: string;
};

type UrlEntity = {
    indices: [number, number];
    display_url: string;
    expanded_url?: string;
    url: string;
};

type MentionEntity = {
    id: number; // id_strが必要では？
    indices: [number, number];
    name: string;
    screen_name: string;
};

type MediaEntity = {
    display_url: string;
    id: number; // id_strが必要では？
    indices: [number, number];
    media_url: string;
    media_url_https: string;
    sizes: MediaSizes | MediaSize[];
    source_status_id?: number; // id_strが必要では？
    type?: MediaType;
    url: string;
    video_info?: VideoInfo;
    ext_alt_text?: string;
};

type MediaType = "photo" | "video" | "animated_gif";

type ResizeMode = "fit" | "crop";

type MediaSize = {
    w: number;
    h: number;
    resize: ResizeMode;
};

type MediaSizes = {
    thumb: MediaSize;
    small: MediaSize;
    medium: MediaSize;
    large: MediaSize;
};

type VideoInfo = {
    aspect_ratio: [number, number];
    duration_millis?: number;
    variants: VideoVariant[];
};

type VideoVariant = {
    bitrate?: number;
    content_type: string;
    url: string;
};

type TwitterUser = {
    contributors_enabled: boolean;
    created_at?: Date;
    default_profile: boolean;
    default_profile_image: boolean;
    description?: string;
    entities?: UserEntities;
    favourites_count?: number;
    follow_request_sent?: boolean;
    followers_count?: number;
    friends_count?: number;
    geo_enabled?: boolean;
    id: number;
    id_str: string;
    is_translator?: boolean;
    lang?: string;
    listed_count?: number;
    location?: string;
    name: string;
    profile_background_color?: string;
    profile_background_image_url?: string;
    profile_background_image_url_https?: string;
    profile_background_tile?: boolean;
    profile_banner_url?: string;
    profile_image_url?: string;
    profile_image_url_https?: string;
    profile_link_color?: string;
    profile_sidebar_border_color?: string;
    profile_sidebar_fill_color?: string;
    profile_text_color?: string;
    profile_use_background_image?: boolean;
    protected: boolean;
    screen_name: string;
    show_all_inline_media?: boolean;
    status?: Tweet;
    statuses_count?: number;
    time_zone?: string;
    url?: string;
    utc_offset?: number;
    verified: boolean;
    withheld_in_countries?: string[];
    withheld_scope?: string;
};

type UserEntities = {
    description: UserEntityDetail;
    url?: UserEntityDetail;
};

type UserEntityDetail = {
    urls: UrlEntity[];
};
